import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useBuilderStore } from '../../stores/builderStore';
import { fieldRegistry } from '../../lib/fieldRegistry';
import type { FormField } from '../../types/form';

interface SortableFieldProps {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
}

function SortableField({ field, isSelected, onSelect, onRemove, onDuplicate }: SortableFieldProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldDef = fieldRegistry[field.type];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative bg-white border-2 rounded-lg p-4 transition-all ${
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-100'
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onSelect}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-2 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing p-1 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
        </svg>
      </div>

      {/* Field Content */}
      <div className="ml-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-sm">
            {fieldDef.icon}
          </span>
          <span className="font-medium text-gray-900">{field.label}</span>
          {field.required && (
            <span className="text-red-500 text-sm">*</span>
          )}
        </div>

        {/* Field Preview */}
        <div className="text-sm text-gray-500">
          {field.type === 'text' && (
            <input
              type="text"
              placeholder={field.placeholder || 'Text input'}
              className="input pointer-events-none"
              disabled
            />
          )}
          {field.type === 'email' && (
            <input
              type="email"
              placeholder={field.placeholder || 'email@example.com'}
              className="input pointer-events-none"
              disabled
            />
          )}
          {field.type === 'number' && (
            <input
              type="number"
              placeholder={field.placeholder || '0'}
              className="input pointer-events-none"
              disabled
            />
          )}
          {field.type === 'textarea' && (
            <textarea
              placeholder={field.placeholder || 'Enter text...'}
              className="input pointer-events-none"
              rows={2}
              disabled
            />
          )}
          {field.type === 'select' && (
            <select className="input pointer-events-none" disabled>
              <option>Select an option...</option>
              {field.options?.map((opt) => (
                <option key={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          {field.type === 'checkbox' && (
            <div className="space-y-1">
              {field.options?.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2">
                  <input type="checkbox" disabled className="rounded" />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
          {field.type === 'radio' && (
            <div className="space-y-1">
              {field.options?.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2">
                  <input type="radio" disabled />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
          {field.type === 'date' && (
            <input type="date" className="input pointer-events-none" disabled />
          )}
          {field.type === 'datetime' && (
            <input type="datetime-local" className="input pointer-events-none" disabled />
          )}
          {field.type === 'file' && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <span className="text-gray-400">Click or drag to upload</span>
            </div>
          )}
          {field.type === 'richtext' && (
            <div className="border border-gray-300 rounded-lg p-3 min-h-[80px] bg-gray-50">
              <span className="text-gray-400">Rich text editor</span>
            </div>
          )}
          {field.type === 'signature' && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center h-24">
              <span className="text-gray-400">Signature pad</span>
            </div>
          )}
          {field.type === 'repeatable' && (
            <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
              <span className="text-gray-400">Repeatable section</span>
            </div>
          )}
          {field.type === 'section' && (
            <div className="border-l-4 border-primary-500 pl-4 py-2 bg-primary-50 rounded-r">
              <h3 className="font-semibold text-gray-800">{field.label || 'Section Title'}</h3>
              {field.properties.sectionDescription && (
                <p className="text-gray-500 text-sm mt-1">{field.properties.sectionDescription}</p>
              )}
            </div>
          )}
          {field.type === 'divider' && (
            <hr
              className="my-2"
              style={{
                borderStyle: field.properties.dividerStyle || 'solid',
                borderColor: field.properties.dividerColor || '#e5e7eb'
              }}
            />
          )}
          {field.type === 'page_break' && (
            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50 text-center">
              <div className="flex items-center justify-center gap-2 text-blue-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium">Page Break</span>
              </div>
              <p className="text-blue-500 text-sm mt-1">{field.label || 'Next Page'}</p>
            </div>
          )}
        </div>

        {field.helpText && (
          <p className="text-xs text-gray-400 mt-2">{field.helpText}</p>
        )}
      </div>

      {/* Actions */}
      <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          title="Duplicate"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
          title="Delete"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function FormCanvas() {
  const { formSchema, selectedFieldId, selectField, removeField, duplicateField } = useBuilderStore();
  const fields = formSchema.fields;

  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas',
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 p-6 overflow-y-auto ${
        isOver ? 'bg-primary-50' : 'bg-gray-100'
      } transition-colors`}
    >
      {fields.length === 0 ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Start building your form</h3>
            <p className="text-gray-500">Drag fields from the left panel to add them here</p>
          </div>
        </div>
      ) : (
        <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3 max-w-2xl mx-auto">
            {fields.map((field) => (
              <SortableField
                key={field.id}
                field={field}
                isSelected={selectedFieldId === field.id}
                onSelect={() => selectField(field.id)}
                onRemove={() => removeField(field.id)}
                onDuplicate={() => duplicateField(field.id)}
              />
            ))}
          </div>
        </SortableContext>
      )}
    </div>
  );
}
